import { useEffect, useRef, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import { authService } from '@/services/AuthService';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Package, Clock, MapPin, ArrowRight, Home, Loader2, AlertCircle, RefreshCw, ShoppingBag, Utensils, Truck, ChefHat, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import KudozDisplay from '@/components/KudozDisplay';
import { useToast } from '@/hooks/use-toast';

interface OrderStatusResponse {
  hash?: string;
  status?: string;
  service?: string;
  restaurant?: {
    id?: number;
    name?: string;
    address?: string;
    phone?: string;
    logo?: string;
  };
  customer?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  delivery?: {
    address?: string;
    latitude?: number;
    longitude?: number;
    instructions?: string;
  };
  items?: Array<{
    id?: string;
    name?: string;
    quantity?: number;
    price?: number;
    modifiers?: any[];
  }>;
  summary?: {
    subtotal?: number;
    tax?: number;
    tip?: number;
    delivery_fee?: number;
    service_fee?: number;
    total?: number;
  };
  timing?: {
    placed?: string;
    estimated_ready?: string;
    estimated_delivery?: string;
  };
  error?: string;
  message?: string;
}

const ORDER_STATUS_STEPS = [
  { key: 'received', label: 'Order Received', icon: ShoppingBag },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'preparing', label: 'Preparing', icon: ChefHat },
  { key: 'ready', label: 'Ready', icon: Utensils },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: MapPin },
];

const PICKUP_STATUS_STEPS = [
  { key: 'received', label: 'Order Received', icon: ShoppingBag },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'preparing', label: 'Preparing', icon: ChefHat },
  { key: 'ready', label: 'Ready for Pickup', icon: Utensils },
  { key: 'picked_up', label: 'Picked Up', icon: CheckCircle },
];

function getStatusIndex(status: string | undefined, isDelivery: boolean): number {
  if (!status) return 0;

  const normalizedStatus = status.toLowerCase().replace(/[_\s-]/g, '');

  const statusMap: Record<string, number> = {
    'received': 0,
    'pending': 0,
    'new': 0,
    'confirmed': 1,
    'accepted': 1,
    'preparing': 2,
    'inprogress': 2,
    'ready': 3,
    'readyforpickup': 3,
    'outfordelivery': 4,
    'intransit': 4,
    'delivered': 5,
    'completed': 5,
    'pickedup': 4,
    'closed': 5,
  };

  return statusMap[normalizedStatus] ?? 0;
}

function formatCurrency(amount: number | undefined): string {
  if (amount === undefined || amount === null) return '$0.00';
  return `$${amount.toFixed(2)}`;
}

function formatTime(dateString: string | undefined): string {
  if (!dateString) return '--:--';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '--:--';
  }
}

export default function OrderSuccess() {
  const [, navigate] = useLocation();
  const [, params] = useRoute('/order-success/:checkoutId/:clientId');

  const checkoutId = params?.checkoutId;
  const clientId = params?.clientId;
  const { toast } = useToast();
  const { isGuestAccount, guestContactEmail, logout, refreshUser } = useAuth();

  const [orderCleared, setOrderCleared] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  // Guest: save-account state
  const [savePassword, setSavePassword] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [accountSaved, setAccountSaved] = useState(false);
  const [saveError, setSaveError] = useState<'endpoint_unavailable' | 'generic' | null>(null);
  const accountSavedRef = useRef(false);

  // Auto-logout guest on unmount unless they converted their account
  useEffect(() => {
    return () => {
      if (isGuestAccount && !accountSavedRef.current) {
        logout();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuestAccount]);

  const handleSaveAccount = async () => {
    if (!savePassword || savePassword.length < 8) {
      toast({ title: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/auth/guest/convert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ password: savePassword }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (err.error === 'ENDPOINT_NOT_DEPLOYED') {
          setSaveError('endpoint_unavailable');
        } else {
          setSaveError('generic');
        }
      } else {
        const convertData = await response.json().catch(() => ({}));
        accountSavedRef.current = true;
        setAccountSaved(true);
        // Clear guest flags from stored auth so subsequent checks treat them as a real user
        try {
          const storedUser = localStorage.getItem('auth_user');
          if (storedUser) {
            const parsed = JSON.parse(storedUser);
            parsed.isGuestAccount = false;
            localStorage.setItem('auth_user', JSON.stringify(parsed));
          }
          // Store updated token if backend rotated it
          if (convertData.token && typeof convertData.token === 'string') {
            localStorage.setItem('auth_token', convertData.token);
          }
          localStorage.removeItem('guestContactEmail');
        } catch {}
        // Refresh auth state so component immediately reflects non-guest status
        refreshUser().catch(() => {});
        toast({ title: 'Account saved!', description: 'Welcome to Eatzy. You can now log in anytime.' });
      }
    } catch {
      setSaveError('generic');
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (!orderCleared) {
      localStorage.removeItem('eatzy-cart');
      localStorage.removeItem('eatzy-order-config');
      setOrderCleared(true);
      console.log('[OrderSuccess] Cleared cart and order config from localStorage');
    }
  }, [orderCleared]);

  useEffect(() => {
    if (checkoutId) {
      try {
        const favorites = JSON.parse(localStorage.getItem('eatzy-favorite-orders') || '[]');
        setIsFavorited(favorites.some((f: any) => f.checkoutId === checkoutId));
      } catch { /* ignore */ }
    }
  }, [checkoutId]);

  const toggleFavoriteOrder = () => {
    if (!checkoutId || !orderData) return;
    try {
      const favorites = JSON.parse(localStorage.getItem('eatzy-favorite-orders') || '[]');
      if (isFavorited) {
        const updated = favorites.filter((f: any) => f.checkoutId !== checkoutId);
        localStorage.setItem('eatzy-favorite-orders', JSON.stringify(updated));
        setIsFavorited(false);
        toast({ title: 'Removed from favorites' });
      } else {
        const entry = {
          checkoutId,
          clientId,
          restaurantName: orderData.restaurant?.name,
          restaurantLogo: orderData.restaurant?.logo,
          items: orderData.items?.map(i => i.name).slice(0, 4) || [],
          total: (orderData as any)?.amounts?.total || orderData.summary?.total || 0,
          serviceType: orderData.service || 'takeout',
          savedAt: Date.now(),
        };
        favorites.unshift(entry);
        const trimmed = favorites.slice(0, 20);
        localStorage.setItem('eatzy-favorite-orders', JSON.stringify(trimmed));
        setIsFavorited(true);
        toast({ title: 'Saved to favorites', description: 'Find it in Quick Reorder on your profile' });
      }
    } catch { /* ignore */ }
  };

  const { data: orderData, isLoading, error, refetch, isRefetching } = useQuery<OrderStatusResponse>({
    queryKey: ['/api/eatzy/orders', checkoutId, 'status', clientId],
    queryFn: async () => {
      console.log('[OrderSuccess] Fetching order status:', { checkoutId, clientId });

      if (!checkoutId || !clientId) {
        console.error('[OrderSuccess] Missing checkoutId or clientId');
        throw new Error('Missing order information');
      }

      const response = await authService.eatzyApiCall(
        `/orders/${checkoutId}/status?client=${encodeURIComponent(clientId)}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[OrderSuccess] API error:', response.status, errorText);
        throw new Error(`Failed to fetch order: ${response.status}`);
      }

      const data = await response.json();
      console.log('[OrderSuccess] Order data received:', JSON.stringify(data, null, 2));
      return data;
    },
    enabled: !!checkoutId && !!clientId,
    refetchInterval: 30000,
    retry: 2,
  });

  const KUDOZ_PER_DOLLAR = 7;
  const subtotalDollars = (orderData as any)?.amounts?.subtotal || 0;
  console.log('[OrderSuccess] Kudoz calculation:', {
    rawSubtotal: (orderData as any)?.amounts?.subtotal,
    subtotalDollars,
    kudozEarned: Math.floor(subtotalDollars * KUDOZ_PER_DOLLAR)
  });
  const kudozEarned = Math.floor(subtotalDollars * KUDOZ_PER_DOLLAR) || 250;

  const isDelivery = (orderData as any)?.type === 'delivery' || orderData?.service === 'delivery';
  const statusSteps = isDelivery ? ORDER_STATUS_STEPS : PICKUP_STATUS_STEPS;
  const visibleSteps = statusSteps.slice(0, isDelivery ? 5 : 4);
  const maxStatusIndex = Math.max(1, visibleSteps.length - 1);
  const currentStatusIndex = Math.min(getStatusIndex(orderData?.status, isDelivery), maxStatusIndex);

  useEffect(() => {
    if (orderData) {
      console.log('[OrderSuccess] Order status:', {
        status: orderData.status,
        service: orderData.service,
        isDelivery,
        currentStatusIndex,
        restaurant: orderData.restaurant?.name,
        itemCount: orderData.items?.length,
        total: orderData.summary?.total
      });
    }
  }, [orderData, isDelivery, currentStatusIndex]);

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <div className="flex-1 p-4 lg:p-6">
          <div className="lg:grid lg:grid-cols-[45%_55%] lg:gap-6 lg:items-start">
            <div className="space-y-4">
              <div className="text-center py-8 lg:py-6">
                <Skeleton className="w-20 h-20 rounded-full mx-auto mb-4" />
                <Skeleton className="h-8 w-48 mx-auto mb-2" />
                <Skeleton className="h-4 w-64 mx-auto" />
              </div>
              <Card>
                <CardContent className="p-4 space-y-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            </div>
            <div className="hidden lg:block space-y-4 mt-0">
              <Card>
                <CardContent className="p-4 space-y-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-24 w-full" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const actionButtons = (
    <>
      <Button
        className="w-full"
        size="lg"
        onClick={() => navigate('/')}
        data-testid="button-back-home"
      >
        <Home className="w-4 h-4 mr-2" />
        Back to Home
      </Button>
      <Button
        variant="outline"
        className="w-full"
        onClick={() => navigate('/profile')}
        data-testid="button-view-orders"
      >
        View Order History
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </>
  );

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 p-4 lg:p-6">
        <div className="lg:grid lg:grid-cols-[45%_55%] lg:gap-6 lg:items-start">

          {/* ── LEFT COLUMN: hero + status card ── */}
          <div className="space-y-4 lg:sticky lg:top-4">

            {/* Hero header */}
            <div className="text-center py-8 lg:py-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Order Confirmed!</h1>
              <p className="text-muted-foreground">
                {orderData?.restaurant?.name
                  ? `Your order from ${orderData.restaurant.name} is being prepared.`
                  : "Thank you for your order. We're preparing it now."
                }
              </p>
            </div>

            {/* Error banner */}
            {error && (
              <Card className="border-destructive">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 text-destructive">
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold">Unable to load order details</div>
                      <div className="text-sm opacity-80">
                        {error instanceof Error ? error.message : 'Please check your order history'}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => refetch()}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Order Status card */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-full">
                      <Package className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">Order Status</div>
                      <div className="text-sm text-muted-foreground capitalize">
                        {orderData?.status?.replace(/_/g, ' ') || 'Processing'}
                      </div>
                    </div>
                  </div>
                  {isRefetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                </div>

                {/* Progress stepper */}
                <div className="py-2">
                  <div className="flex items-center justify-between relative">
                    {visibleSteps.map((step, index) => {
                      const isCompleted = index <= currentStatusIndex;
                      const isCurrent = index === currentStatusIndex;
                      const Icon = step.icon;

                      return (
                        <div key={step.key} className="flex flex-col items-center z-10 flex-1">
                          <div
                            className={`
                              w-8 h-8 rounded-full flex items-center justify-center transition-colors
                              ${isCompleted
                                ? 'bg-green-500 text-white'
                                : 'bg-muted text-muted-foreground'
                              }
                              ${isCurrent ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-background' : ''}
                            `}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className={`text-xs mt-1 text-center ${isCompleted ? 'text-green-600 dark:text-green-400 font-medium' : 'text-muted-foreground'}`}>
                            {step.label}
                          </div>
                        </div>
                      );
                    })}
                    <div className="absolute top-4 left-0 right-0 h-0.5 bg-muted -z-0 mx-8">
                      <div
                        className="h-full bg-green-500 transition-all duration-500"
                        style={{ width: `${(currentStatusIndex / maxStatusIndex) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Estimated delivery / ready */}
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                    <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">
                      {isDelivery ? 'Estimated Delivery' : 'Estimated Ready'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {orderData?.timing?.estimated_delivery
                        ? formatTime(orderData.timing.estimated_delivery)
                        : orderData?.timing?.estimated_ready
                        ? formatTime(orderData.timing.estimated_ready)
                        : '25-35 min'
                      }
                    </div>
                  </div>
                </div>

                {/* Order reference */}
                {checkoutId && (
                  <>
                    <Separator />
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                        <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">Order Reference</div>
                        <div className="text-sm text-muted-foreground font-mono">
                          #{checkoutId.substring(0, 8).toUpperCase()}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── RIGHT COLUMN: items, rewards, actions ── */}
          <div className="space-y-4 mt-4 lg:mt-0">

            {/* Order items + billing */}
            {orderData?.items && orderData.items.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <div className="font-semibold mb-3">Order Items</div>
                  <div className="space-y-2">
                    {orderData.items.map((item, index) => (
                      <div key={item.id || index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{item.quantity}x</span>
                          <span>{item.name}</span>
                        </div>
                        <span className="text-muted-foreground">
                          {formatCurrency(item.price ? item.price * (item.quantity || 1) : 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                  {((orderData as any)?.amounts || orderData?.summary) && (
                    <>
                      <Separator className="my-3" />

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatCurrency((orderData as any)?.amounts?.subtotal || orderData?.summary?.subtotal)}</span>
                      </div>

                      {(() => {
                        const fees = (orderData as any)?.fees || [];
                        const deliveryFee = fees.find((f: any) =>
                          f.name?.toLowerCase().includes('delivery')
                        );
                        if (deliveryFee?.amount > 0) {
                          return (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{deliveryFee.name || 'Delivery Fee'}</span>
                              <span>{formatCurrency(deliveryFee.amount)}</span>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {(() => {
                        const fees = (orderData as any)?.fees || [];
                        const taxes = (orderData as any)?.taxes || [];
                        const otherFees = fees.filter((f: any) =>
                          !f.name?.toLowerCase().includes('delivery') &&
                          f.name?.toLowerCase() !== 'gratuity' &&
                          !f.name?.toLowerCase().includes('tip')
                        );
                        const otherFeesTotal = otherFees.reduce((sum: number, f: any) => sum + (f.amount || 0), 0);
                        const taxTotal = taxes.reduce((sum: number, t: any) => sum + (t.amount || 0), 0);
                        const taxesAndFeesTotal = taxTotal + otherFeesTotal;
                        if (taxesAndFeesTotal > 0) {
                          return (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Taxes & Fees</span>
                              <span>{formatCurrency(taxesAndFeesTotal)}</span>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {(() => {
                        const fees = (orderData as any)?.fees || [];
                        const tipFee = fees.find((f: any) =>
                          f.name?.toLowerCase() === 'gratuity' ||
                          f.name?.toLowerCase().includes('tip')
                        );
                        if (tipFee?.amount > 0) {
                          return (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Tip</span>
                              <span>{formatCurrency(tipFee.amount)}</span>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      <Separator className="my-2" />

                      <div className="flex items-center justify-between font-semibold">
                        <span>Total</span>
                        <span>{formatCurrency((orderData as any)?.amounts?.total || orderData?.summary?.total)}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Kudoz Earned */}
            <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-orange-200 dark:border-orange-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-orange-800 dark:text-orange-200">Kudoz Earned!</div>
                    <div className="text-sm text-orange-600 dark:text-orange-400">
                      Points will be added to your account
                    </div>
                  </div>
                  <KudozDisplay amount={kudozEarned} size="lg" />
                </div>
              </CardContent>
            </Card>

            {/* Save for Quick Reorder */}
            {orderData && (
              <Card
                className={`cursor-pointer hover-elevate active-elevate-2 ${isFavorited ? 'border-red-400 dark:border-red-600' : ''}`}
                onClick={toggleFavoriteOrder}
                data-testid="card-favorite-order"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full transition-colors ${isFavorited ? 'bg-red-100 dark:bg-red-900/30' : 'bg-muted'}`}>
                      <Heart className={`w-5 h-5 transition-colors ${isFavorited ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{isFavorited ? 'Saved to Favorites' : 'Save for Quick Reorder'}</div>
                      <div className="text-sm text-muted-foreground">
                        {isFavorited ? 'This order will appear first in your Quick Reorder list' : 'Tap to save this order for easy reordering later'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Guest: Save your account */}
            {isGuestAccount && (
              <Card className="border-[#0D9C97]/40 bg-[#0D9C97]/5">
                <CardContent className="p-4 space-y-3">
                  {accountSaved ? (
                    <div className="text-center space-y-1">
                      <div className="font-semibold text-[#0D9C97]">Account saved!</div>
                      <p className="text-sm text-muted-foreground">
                        You can now log in with {guestContactEmail} anytime.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <div className="font-semibold">Save your account</div>
                        <p className="text-sm text-muted-foreground">
                          Set a password to keep your order history and earn Kudoz on future orders.
                        </p>
                        {guestContactEmail && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Your account email: <span className="font-medium">{guestContactEmail}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          placeholder="Choose a password"
                          value={savePassword}
                          onChange={e => setSavePassword(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-[#0D9C97]/50"
                          disabled={saveLoading}
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveAccount}
                          disabled={saveLoading || !savePassword}
                          className="bg-[#0D9C97] text-white whitespace-nowrap"
                        >
                          {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                      {saveError === 'endpoint_unavailable' && (
                        <div className="flex items-start gap-2 rounded-md bg-muted p-3">
                          <AlertCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                          <p className="text-sm text-muted-foreground">
                            Your order was placed successfully. Account saving is temporarily unavailable — you'll be able to access your order history once this feature is enabled.
                          </p>
                        </div>
                      )}
                      {saveError === 'generic' && (
                        <div className="flex items-center gap-2 text-sm text-destructive">
                          <AlertCircle className="w-4 h-4 shrink-0" />
                          <span>Could not save account. Please try again.</span>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* SMS / contact note */}
            <div className="text-center text-sm text-muted-foreground">
              <p>You'll receive updates about your order via SMS.</p>
              {orderData?.restaurant?.phone && (
                <p className="mt-1">
                  Questions? Call {orderData.restaurant.name || 'the restaurant'}: {orderData.restaurant.phone}
                </p>
              )}
            </div>

            {/* Desktop inline action buttons */}
            <div className="hidden lg:flex lg:flex-col gap-3">
              {actionButtons}
            </div>
          </div>

        </div>
      </div>

      {/* Mobile sticky footer — hidden on desktop */}
      <div className="sticky bottom-0 p-4 bg-background border-t space-y-3 lg:hidden">
        {actionButtons}
      </div>
    </div>
  );
}
